import {AceLoggerService} from '@logging/ace-logger.service';
import {ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, Optional, Self, SimpleChanges, Output, EventEmitter} from '@angular/core';
import {ControlValueAccessor, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, NgControl, ValidationErrors} from '@angular/forms';
import moment, {Moment} from 'moment-timezone';
import {PlacementArray} from '@ng-bootstrap/ng-bootstrap/util/positioning';
import {AceLogger} from '@logging/ace-logger';
import {filter, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {momentsEqual} from '@shared/util/comparitors/moment-comparator';
import {objectEqualMomentAware} from '@shared/util/comparitors/objects-comparator';
import {DateTimeSelectComponentErrorKey} from '@shared/components/date-time-select/date-time-select.component';
import {I18nMessage} from '@shared/api/model/i18n';
import {DateTimeRangeService} from '@shared/components/date-time-range-picker/date-time-range.service';
import {ValidatorUtils} from '@shared/util/validators/validator-utils';
import {NgbDatepickerNavigateEvent} from "@ng-bootstrap/ng-bootstrap";

export enum DateSelectComponentFields {
  START_DATE = 'startDate',
  END_DATE = 'endDate'
}

export interface DateTimeRange {
  [DateSelectComponentFields.START_DATE]: Moment;
  [DateSelectComponentFields.END_DATE]: Moment;
}

export interface DateTimeRangeLabel {
  [DateSelectComponentFields.START_DATE]: I18nMessage;
  [DateSelectComponentFields.END_DATE]: I18nMessage;
}

enum ControlNames {
  START_DATE_CONTROL_NAME = 'startDate',
  END_DATE_CONTROL_NAME = 'endDate',
}

@Component({
  selector: 'app-date-time-range-select',
  templateUrl: './date-time-range-select.component.html',
  styleUrls: ['./date-time-range-select.component.scss'],
  standalone: false
})
export class DateTimeRangeSelectComponent implements OnChanges, OnInit, OnDestroy, ControlValueAccessor {

  static readonly START_DATE_BEFORE_END_DATE_ERROR_KEY = 'endDateBeforeStartDate';
  /**
   * Can be used to associate errors with the startDate or endDate control. The errors will be shown underneath the relevant control.
   * To do this, add a {@link ValidationErrors} object with key '{@link ControlNames.START_DATE_CONTROL_NAME} + {@link COPY_POSTFIX_ERROR_KEY}'
   * or '{@link ControlNames.END_DATE_CONTROL_NAME} + {@link COPY_POSTFIX_ERROR_KEY}' to the control associated with this component.
   */
  static readonly COPY_POSTFIX_ERROR_KEY = 'Copy';

  static readonly DEFAULT_LABEL: DateTimeRangeLabel = {
    [DateSelectComponentFields.START_DATE]: {messageKey: 'Start Date'},
    [DateSelectComponentFields.END_DATE]: {messageKey: 'End Date'}
  };

  @Input() label: DateTimeRangeLabel = DateTimeRangeSelectComponent.DEFAULT_LABEL;
  @Input() minDate: Moment;
  @Input() maxDate: Moment;
  @Input() readOnly = false;

  /**
   * Determines what fields must be set before this component is deemed to have a value.
   * For example, if this property is set to [{@link DateSelectComponentFields.START_DATE}, {@link DateSelectComponentFields.END_DATE}],
   * then this component will only yield a value when both start date and end date have a value, null otherwise.
   *
   * If set to [{@link DateSelectComponentFields.START_DATE}], then the value will be yielded if start date has a value, or null otherwise,
   * irrespective to what end date value is.
   */
  @Input() requiredFields: DateSelectComponentFields[] = [DateSelectComponentFields.START_DATE, DateSelectComponentFields.END_DATE];
  @Input() showTime = false;
  @Input() placement: PlacementArray = 'auto';
  @Input() timezone: string;
  /**
   * Whether startDate is allowed to be equal to the endDate
   */
  @Input() allowEqual = true;

  /**
   * Determines whether this component will display its own errors.
   *
   * The errors are shown under the most relevant input field, so it's nice :)
   */
  @Input() showErrors = true;

  /* help text displayed under dates */
  @Input() startDateHelpText: string;
  @Input() endDateHelpText: string;

  /** Whether to vertically stack controls or show them side by side */
  @Input() wrapControls = false;
  /** Whether to try and expand to fill all the available horizontal space, or only as much as needed */
  @Input() fillHorizontalWidth = false;

  @Output() navigate = new EventEmitter<{field: DateSelectComponentFields, data: NgbDatepickerNavigateEvent}>();
  @Output() closed = new EventEmitter<{field: DateSelectComponentFields}>();

  readonly ControlNames = ControlNames;
  readonly controls: UntypedFormGroup;

  /**
   * Max between {@link minDate} and the chosen startDate. To ensure the startDate is not later than endDate.
   */
  maxDateForStartDate: Moment;

  /**
   * Min between {@link maxDate} and the chosen endDate. To ensure the endDate is not earlier than startDate.
   */
  minDateForEndDate: Moment;
  effectiveTimezone = moment.tz.guess();

  private readonly DATE_FORMAT = 'YYYY-MM-DD';
  private readonly DATE_TIME_FORMAT = this.DATE_FORMAT + ' HH:mm:ss';
  private readonly ngUnsubscribe = new Subject<void>();
  private readonly logger: AceLogger;

  /**
   * To help the user choose dates such that the startDate is before the endDate, the {@link NgbInputDatepicker.minDate} and
   * {@link NgbInputDatepicker.maxDate} constraints on the startDate and endDate controls change, as the value of these controls changes.
   * When the min and max constraints get updated, the {@link NgbInputDatepicker} calls onValidatorChange() function, which due to the
   * angular bug https://github.com/angular/angular/issues/25749 makes the {@link controls.valueChanges} observable emit a value,
   * even if the value has not changed. When this happens during the initialisation of this control, it causes ng-pristine property of this
   * control to change, which leads to ExpressionChangedAfterItHasBeenCheckedError.
   *
   * Also, distinctUntilChanged cannot be used on the {@link controls.valueChanges} observable, because the first time the observable
   * emits a value, distinctUntilChanged let's it through. In our case, because the call to onValidatorChange() happens during component
   * initialisation, the first value that distinctUntilChanged sees is what causes the ExpressionChangedAfterItHasBeenCheckedError.
   *
   * Therefore, we are forced to "manually" keep track of the old value of the control in the {@link writeValue} function and in the
   * subscription to the {@link controls.valueChanges} observable.
   */
  private currentValue: any = null;
  private onChangeFn: (value: DateTimeRange) => void = (_: DateTimeRange) => {};
  private onTouchedFn: () => void = () => {};

  constructor(@Optional() @Self() private controlDir: NgControl,
              private dateTimeRangeService: DateTimeRangeService,
              private cdr: ChangeDetectorRef,
              fb: UntypedFormBuilder,
              logService: AceLoggerService) {
    if (controlDir) {
      controlDir.valueAccessor = this;
    }
    this.logger = logService.create(DateTimeRangeSelectComponent.name);

    // If the FormControl that is bound to this component (i.e. controlDir.control) is initialised with a default value when constructed
    // the writeValue method is called before ngOnInit or ngOnChanges. Hence, we need to:
    // 1. Build the controls form group so that we can assign the value to it when writeValue method is invoked
    // 2. Construct ngbDateAdapter with the "default" timezone. If this component is given a different timezone value later, it will be
    // rebuilt in ngOnChanges.
    this.controls = fb.group({
      [ControlNames.START_DATE_CONTROL_NAME]: new UntypedFormControl(),
      [ControlNames.END_DATE_CONTROL_NAME]: new UntypedFormControl(),
    });
  }

  ngOnInit() {
    this.controls.valueChanges.pipe(filter(value => !objectEqualMomentAware(this.currentValue, value)), takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.updateStartDateMaxFieldsBound(value ? value[DateSelectComponentFields.END_DATE] : null, this.minDate, this.maxDate);
        this.updateEndDateMinFieldsBound(value ? value[DateSelectComponentFields.START_DATE] : null, this.minDate, this.maxDate);
        this.currentValue = value;
        if (this.onChangeFn) {
          this.onChangeFn(this.hasAllRequiredFields(value) ? value : null);
        }
      });

    this.controls.statusChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(_ => {
      const startDateControl = this.controls.get(ControlNames.START_DATE_CONTROL_NAME);
      const endDateControl = this.controls.get(ControlNames.END_DATE_CONTROL_NAME);
      const control = this.controlDir && this.controlDir.control;
      const startDateErrors: ValidationErrors = startDateControl.errors || {};
      const endDateErrors: ValidationErrors = endDateControl.errors || {};
      const errors = control.errors || {};

      let updateStartErrors = false;
      let updateEndErrors = false;
      const startDate: Moment = startDateControl.value;
      if (startDate) {
        if (this.maxDate && startDate.isAfter(this.maxDate)) {
          startDateErrors[DateTimeSelectComponentErrorKey.AFTER_MAX_DATE] = {
            messageKey: 'Choose date earlier than',
            parameters: {date: this.formatTimestamp(this.maxDate)}
          };
          updateStartErrors = true;
        } else if (startDateErrors[DateTimeSelectComponentErrorKey.AFTER_MAX_DATE]) {
          delete startDateErrors[DateTimeSelectComponentErrorKey.AFTER_MAX_DATE];
          updateStartErrors = true;
        }
      }

      const endDate: Moment = endDateControl.value;
      if (endDate) {
        if (this.minDate && endDate.isBefore(this.minDate)) {
          endDateErrors[DateTimeSelectComponentErrorKey.BEFORE_MIN_DATE] = {
            messageKey: 'Choose date earlier than',
            parameters: {date: this.formatTimestamp(this.minDate)}
          };
          updateEndErrors = true;
        } else if (endDateErrors[DateTimeSelectComponentErrorKey.BEFORE_MIN_DATE]) {
          delete endDateErrors[DateTimeSelectComponentErrorKey.BEFORE_MIN_DATE];
          updateEndErrors = true;
        }
      }

      if (startDate && endDate) {
        if (startDate.isAfter(endDate) || (!this.allowEqual && !startDate.isBefore(endDate))) {
          startDateErrors[DateTimeRangeSelectComponent.START_DATE_BEFORE_END_DATE_ERROR_KEY] = {messageKey: 'Start Date should be earlier than End Date'};
          updateStartErrors = true;
        } else if (startDateErrors[DateTimeRangeSelectComponent.START_DATE_BEFORE_END_DATE_ERROR_KEY]) {
          delete startDateErrors[DateTimeRangeSelectComponent.START_DATE_BEFORE_END_DATE_ERROR_KEY];
          updateEndErrors = true;
        }
      }

      if (errors.required && this.requiredFields) {
        if (this.requiredFields.includes(DateSelectComponentFields.START_DATE)) {
          startDateErrors.required = true;
          updateStartErrors = true;
        }
        if (this.requiredFields.includes(DateSelectComponentFields.END_DATE)) {
          endDateErrors.required = true;
          updateEndErrors = true;
        }
      } else {
        if (startDateErrors.required) {
          delete startDateErrors.required;
        }
        if (endDateErrors.required) {
          delete endDateErrors.required
        }
      }

      // Copy any error messages from the control to start date control for display
      // Useful if there are any validators used by the outer form, the error messages of which should be displayed under the startDate
      // control
      let potentialErrorsToCopy = errors[ControlNames.START_DATE_CONTROL_NAME + DateTimeRangeSelectComponent.COPY_POSTFIX_ERROR_KEY];
      if (typeof potentialErrorsToCopy === 'object') {
        Object.keys(potentialErrorsToCopy).forEach(key => {
          startDateErrors[key] = potentialErrorsToCopy[key];
          updateStartErrors = true;
        });
      }

      // Copy any error messages from the control to end date control for display
      // Useful if there are any validators used by the outer form, the error messages of which should be displayed under the endDate
      // control
      potentialErrorsToCopy = errors[ControlNames.END_DATE_CONTROL_NAME + DateTimeRangeSelectComponent.COPY_POSTFIX_ERROR_KEY];
      if (typeof potentialErrorsToCopy === 'object') {
        Object.keys(potentialErrorsToCopy).forEach(key => {
          endDateErrors[key] = potentialErrorsToCopy[key];
          updateEndErrors = true;
        });
      }

      if (updateStartErrors) {
        startDateControl.setErrors(ValidatorUtils.normaliseErrors(startDateErrors), {emitEvent: false});
      }
      if (updateEndErrors) {
        endDateControl.setErrors(ValidatorUtils.normaliseErrors(endDateErrors), {emitEvent: false});
      }

      if (control) {
        if (startDateErrors && Object.keys(startDateErrors).length) {
          errors[DateSelectComponentFields.START_DATE] = startDateErrors;
        } else {
          delete errors[DateSelectComponentFields.START_DATE];
        }
        if (endDateErrors && Object.keys(endDateErrors).length) {
          errors[DateSelectComponentFields.END_DATE] = endDateErrors;
        } else {
          delete errors[DateSelectComponentFields.END_DATE];
        }
        control.setErrors(ValidatorUtils.normaliseErrors(errors));
        this.cdr.detectChanges();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.timezone) {
      this.effectiveTimezone = this.timezone ? this.timezone : moment.tz.guess();
    }

    if (changes.minDate) {
      this.updateEndDateMinFieldsBound(this.controls.get(ControlNames.START_DATE_CONTROL_NAME).value, this.minDate, this.maxDate);
    }

    if (changes.maxDate) {
      this.updateStartDateMaxFieldsBound(this.controls.get(ControlNames.END_DATE_CONTROL_NAME).value, this.minDate, this.maxDate);
    }
  }

  formatTimestamp(ts: Moment): string {
    return this.dateTimeRangeService.formatTimestamp(
      ts,
      this.effectiveTimezone,
      this.showTime,
      this.DATE_FORMAT,
      this.DATE_TIME_FORMAT
    );
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  writeValue(value: DateTimeRange): void {
    this.logger.debug('writeValue', value);

    this.updateStartDateMaxFieldsBound(value && value[DateSelectComponentFields.END_DATE], this.minDate, this.maxDate);
    this.updateEndDateMinFieldsBound(value && value[DateSelectComponentFields.START_DATE], this.minDate, this.maxDate);

    const v = value ? value : {[ControlNames.START_DATE_CONTROL_NAME]: null, [ControlNames.END_DATE_CONTROL_NAME]: null};
    this.currentValue = v;
    this.controls.setValue(v, {emitEvent: false});
  }

  setDisabledState(disabled: boolean) {
    if (disabled) {
      this.controls.disable({emitEvent: false});
    } else {
      this.controls.enable({emitEvent: false});
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onTouched(): void {
    if (this.onTouchedFn) {
      this.onTouchedFn();
    }
  }

  onNavigate(field: ControlNames, event: NgbDatepickerNavigateEvent) {
    this.navigate.emit({field: this.getFieldName(field), data: event});
  }

  onClosed(field: ControlNames) {
    this.closed.emit({field: this.getFieldName(field)});
  }

  getOtherErrors(): ValidationErrors {
    if (ValidatorUtils.displayErrors(this.controlDir?.control)) {
      const errors = Object.assign({}, this.controlDir?.control.errors);
      // All the errors below are displayed under the actual controls
      delete errors[ControlNames.START_DATE_CONTROL_NAME];
      delete errors[ControlNames.END_DATE_CONTROL_NAME];
      delete errors.required;
      return ValidatorUtils.normaliseErrors(errors);
    } else {
      return null;
    }
  }

  private calculateBound(value: Moment, bound: Moment, func: (...Moment) => Moment): Moment {
    const values = [value, bound].filter(v => moment.isMoment(v));
    return values && values.length ? func(values) : null;
  }

  private updateStartDateMaxFieldsBound(endDate: Moment, minDate: Moment, maxDate: Moment): void {
    const endDateValue = !this.allowEqual && !this.showTime && moment.isMoment(endDate) && endDate.isValid()? endDate.clone().subtract(1, 'days') : endDate;
    const value = this.calculateBound(endDateValue, maxDate, moment.min);
    // Datepicker blows up when min date is set after max date, so check for this
    if (!momentsEqual(this.maxDateForStartDate, value) && (!value || !minDate || !value.isBefore(minDate))) {
      this.maxDateForStartDate = value;
    }
  }

  private updateEndDateMinFieldsBound(startDate: Moment, minDate: Moment, maxDate: Moment): void {
    const startDateValue = !this.allowEqual && !this.showTime && moment.isMoment(startDate) && startDate.isValid()? startDate.clone().add(1, 'days') : startDate;
    const value = this.calculateBound(startDateValue, minDate, moment.max);
    // Datepicker blows up when min date is set after max date, so check for this
    if (!momentsEqual(this.minDateForEndDate, value) && (!value || !maxDate || !value.isAfter(maxDate))) {
      this.minDateForEndDate = value;
    }
  }

  private hasAllRequiredFields(value: DateTimeRange): boolean {
    return this.requiredFields.every(fieldName => value[fieldName] != null);
  }

  private getFieldName(controlName: ControlNames): DateSelectComponentFields {
    switch (controlName) {
      case ControlNames.START_DATE_CONTROL_NAME:
        return DateSelectComponentFields.START_DATE;
      case ControlNames.END_DATE_CONTROL_NAME:
        return DateSelectComponentFields.END_DATE;
      default:
        throw new Error('Unknown control name ' + controlName);
    }
  }
}
